import Immutable from 'immutable';
import { combineReducers } from 'redux-immutable';
import get from 'lodash/get';

import * as socketConstants from '../../../../api/connect/socket/constants';
import describe from '../../conversation/describe/ChatConversationDescribe/reducer';
import entities from './reducer_entities';
import sound from '../ChatAppSound/reducer';
import treePicker from '../../form/ChatFormTreePicker/reducer';

import {
  CHAT_UPDATE_SESSION,
  CHAT_UPDATE_SESSIONS,
  CHAT_NEW_SESSION,
  CHAT_SCROLL_TO_MESSAGE_ID,
  CHAT_RESET_SCROLL_TO_MESSAGE_ID,
  CHAT_FORM_POST,
  CHAT_IS_AFTER_HOURS,
  CHAT_VOLUME_CONTROL_START,
  CHAT_VOLUME_CONTROL_STOP,
  CHAT_VOLUME_CONTROL_POST,
  CHAT_UPDATE_COMPANY,
  INIT_SINGLE_CHAT,
  UPDATE_SINGLE_CHAT,
  LAST_ACTIVITY,
  CHAT_MESSAGE_RECEIVED,
  SESSION_STATE,
  SET_FOCUS,
} from './actions';

import { CHAT_ROUTE_TO_HISTORY, CHAT_ROUTE_TO_CONVERSATION, CHAT_ROUTE_TO_FORM } from './actions_routing';

const focus = (state = '', action) => {
  switch (action.type) {
    case SET_FOCUS: {
      return action.data;
    }
    default: {
      return state;
    }
  }
};

const activeSessionId = (state = '', action) => {
  switch (action.type) {
    case CHAT_ROUTE_TO_CONVERSATION: {
      return action.sessionId;
    }
    case CHAT_NEW_SESSION: {
      if (!action.session) return state;

      return action.session.id;
    }
    case CHAT_FORM_POST:
    case CHAT_VOLUME_CONTROL_POST: {
      const sessionId = get(action, 'form.session.id');
      return sessionId;
    }
    default: {
      return state;
    }
  }
};

const route = (state = '', action) => {
  switch (action.type) {
    case CHAT_ROUTE_TO_HISTORY: {
      return 'history';
    }
    case CHAT_ROUTE_TO_CONVERSATION: {
      return 'conversation';
    }
    case CHAT_ROUTE_TO_FORM: {
      return 'form';
    }
    default: {
      return state;
    }
  }
};

const scrollToMessageId = (state = '', action) => {
  switch (action.type) {
    case CHAT_SCROLL_TO_MESSAGE_ID: {
      return action.messageId;
    }
    case CHAT_VOLUME_CONTROL_POST: {
      const messages = get(action, 'form.session.messages', Immutable.OrderedSet());
      const lastMessageId = messages.last();
      return lastMessageId;
    }
    case CHAT_RESET_SCROLL_TO_MESSAGE_ID: {
      return '';
    }
    default: {
      return state;
    }
  }
};

const sessions = (state = Immutable.OrderedSet(), action) => {
  switch (action.type) {
    case CHAT_NEW_SESSION: {
      if (!action.session) return state;
      const leftSet = Immutable.OrderedSet([action.session.id]);
      return leftSet.union(state);
    }
    case CHAT_UPDATE_SESSIONS: {
      const { sessions } = action; // eslint-disable-line no-shadow
      if (!sessions) return state;

      return sessions.reduce((s, session) => s.add(session.id), state);
    }
    case CHAT_UPDATE_SESSION: {
      const { session } = action; // eslint-disable-line no-shadow
      if (!session) return state;

      return state.add(session.id);
    }
    case CHAT_FORM_POST:
    case CHAT_VOLUME_CONTROL_POST: {
      const sessionId = get(action, 'form.session.id');
      return state.add(sessionId);
    }
    default: {
      return state;
    }
  }
};

const volumeControlSessions = (state = Immutable.OrderedSet(), action) => {
  switch (action.type) {
    case CHAT_VOLUME_CONTROL_START: {
      if (!action.sessionId) return state;
      const leftSet = Immutable.OrderedSet([action.sessionId]);
      return leftSet.union(state);
    }
    case CHAT_VOLUME_CONTROL_STOP: {
      if (!action.sessionId) return state;
      return state.delete(action.sessionId);
    }
    case CHAT_MESSAGE_RECEIVED: {
      const sessionId = get(action, 'message.sessionId');
      const messageType = get(action, 'message.sentBy.type');
      const isFromAgent = messageType === 'user';
      if (!isFromAgent) return state;

      if (!sessionId || !state.includes(sessionId)) return state;

      return state.delete(sessionId);
    }
    default: {
      return state;
    }
  }
};

const isAfterHours = (state = false, action) => {
  switch (action.type) {
    case CHAT_IS_AFTER_HOURS: {
      return action.isAfterHours;
    }
    default: {
      return state;
    }
  }
};

export const singleChat = (state = Immutable.Map({ enabled: false, sessionId: '' }), action) => {
  switch (action.type) {
    case CHAT_UPDATE_COMPANY: {
      const enabled = get(action, 'company.singleSessionChat', false);

      return state.merge({ enabled });
    }
    case INIT_SINGLE_CHAT: {
      const sessionId = get(action, 'activeSessionId', '');

      return state.merge({
        sessionId,
      });
    }
    case UPDATE_SINGLE_CHAT: {
      const sessionId = get(action, 'activeSessionId', '');

      return state.merge({
        sessionId,
      });
    }
    case CHAT_FORM_POST: {
      const sessionId = get(action, 'form.session.id', '');

      return state.merge({
        sessionId,
      });
    }
    case CHAT_NEW_SESSION: {
      const sessionId = get(action, 'session.id', '');

      return state.merge({
        sessionId,
      });
    }
    case CHAT_UPDATE_SESSION: {
      const newSession = get(action, 'session', {});

      if (state.get('sessionId') === newSession.id && newSession.lockedAt) {
        return state.set('sessionId', '');
      }

      return state;
    }
    default: {
      return state;
    }
  }
};

export const lastActivity = (state = null, action) => {
  switch (action.type) {
    case LAST_ACTIVITY: {
      return action.activity;
    }
    default: {
      return state;
    }
  }
};

export const sessionStates = (state = null, action) => {
  switch (action.type) {
    case SESSION_STATE: {
      return action.sessionStates;
    }
    default:
      return state;
  }
};

export const socketActivity = (state = Immutable.Map({}), action) => {
  const status = get(action, 'data.status');
  const id = get(action, 'data.id');
  const type = get(action, 'type');

  switch (type) {
    case socketConstants.CHAT_ACTIVITY_TYPING_EVENT_NAME: {
      return state.setIn([id, 'typingStatus'], status);
    }
    default:
      return state;
  }
};

const Chat = combineReducers({
  entities,
  route,
  activeSessionId,
  describe,
  sound,
  scrollToMessageId,
  sessions,
  sessionStates,
  isAfterHours,
  volumeControlSessions,
  singleChat,
  treePicker,
  lastActivity,
  socketActivity,
  focus,
});

export default Chat;
